import { useState, useEffect, createContext, useContext } from "react";
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import apiClient, { API } from "./utils/apiClient";
import CookieConsent from "./components/CookieConsent";
import TrustSplash from "./components/TrustSplash";
import AnnouncementBanner from "./components/AnnouncementBanner";
import MaintenanceMode from "./components/MaintenanceMode";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AnalyticsTracker from "./components/AnalyticsTracker";

// Export API and apiClient for use in components
export { API, apiClient };

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Dashboard from "./pages/Dashboard";
import Upload from "./pages/Upload";
import Ask from "./pages/AskCaseReview";
import History from "./pages/History";
import SavedAnswers from "./pages/SavedAnswers";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Pricing from "./pages/Pricing";
import Billing from "./pages/Billing";
import VerifyEmail from "./pages/VerifyEmail";

// Admin Pages
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./components/AdminLayout";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import Faq from "./pages/Faq";
import HowItWorks from "./pages/HowItWorks";
import Security from "./pages/Security";
import CbaiqVsChatgpt from "./pages/CbaiqVsChatgpt";
import AdminOverview from "./pages/AdminOverview";
import AdminUsers from "./pages/AdminUsers";
import AdminCredits from "./pages/AdminCredits";
import AdminBilling from "./pages/AdminBilling";
import AdminPromotions from "./pages/AdminPromotions";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminAnalyticsDashboard from "./pages/AdminAnalyticsDashboard";
import AdminAudit from "./pages/AdminAudit";
import AdminContracts from "./pages/AdminContracts";
import AdminQA from "./pages/AdminQA";
import AdminCaseReview from "./pages/AdminCaseReview";
import AdminSettings from "./pages/AdminSettings";
import AdminSupport from "./pages/AdminSupport";
import AdminTeam from "./pages/AdminTeam";

// SaaS Intelligence Admin Pages
import AdminSaaSOverview from "./pages/AdminSaaSOverview";
import AdminActivation from "./pages/AdminActivation";
import AdminCreditProfitability from "./pages/AdminCreditProfitability";
import AdminChurnRisk from "./pages/AdminChurnRisk";
import AdminAPIKeys from "./pages/admin/AdminAPIKeys";
import AdminAgentAnalytics from "./pages/AdminAgentAnalytics";

// Blog Pages
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import ContactSales from "./pages/ContactSales";
import AdminPricing from "./pages/AdminPricing";
import AdminSalesLeads from "./pages/AdminSalesLeads";
import Checkout from "./pages/Checkout";
import BillingSuccess from "./pages/BillingSuccess";
import OrgDashboard from "./pages/OrgDashboard";
import AcceptInvite from "./pages/AcceptInvite";

// Admin Blog Pages
import AdminBlogList from "./pages/AdminBlogList";
import AdminBlogEditor from "./pages/AdminBlogEditor";
import AdminAIBlogSettings from "./pages/AdminAIBlogSettings";
import AdminOrgs from "./pages/AdminOrgs";

// Agent Pages (Contract Expert)
import AgentChat from "./pages/agent/AgentChat";
import AgentDocuments from "./pages/agent/AgentDocuments";
import ConnectChannels from "./pages/agent/ConnectChannels";
import ContractExpertWelcome from "./components/agent/ContractExpertWelcome";
import AgentPricing from "./pages/agent/AgentPricing";
import AgentBillingSuccess from "./pages/agent/AgentBillingSuccess";
import AdminCustomerSuccess from "./pages/AdminCustomerSuccess";
import AdminAgentSupport from "./pages/AdminAgentSupport";
import PilotReadinessDashboard from "./pages/PilotReadinessDashboard";

// Modals
import { InsufficientCreditsModal } from "./components/InsufficientCreditsModal";
import { PlanRequiredModal } from "./components/PlanRequiredModal";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await apiClient.get('/auth/me', {
            skipErrorToast: true // Suppress error toast for this silent check
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    // Use axios directly for login to handle errors in the Login component
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    // Use axios directly for register to handle errors in the Register component
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const acceptDisclaimer = async () => {
    await apiClient.post('/auth/accept-disclaimer', { accepted: true });
    setUser({ ...user, disclaimer_accepted: true });
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await apiClient.get('/auth/me', {
          skipErrorToast: true
        });
        setUser(response.data);
        console.log('[AuthContext] User data refreshed');
      } catch (error) {
        console.error('[AuthContext] Failed to refresh user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, acceptDisclaimer, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Disclaimer Modal wrapper
const DisclaimerWrapper = ({ children }) => {
  const { user, acceptDisclaimer } = useAuth();
  // Use user.disclaimer_accepted directly to control modal visibility
  const showDisclaimer = user && !user.disclaimer_accepted;

  const handleAccept = async () => {
    await acceptDisclaimer();
    toast.success("Disclaimer accepted");
  };

  if (showDisclaimer) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm">
        <div className="bg-card border border-border rounded-md shadow-lg max-w-lg mx-4 p-6" data-testid="disclaimer-modal">
          <h2 className="text-xl font-heading font-semibold mb-4">Important Notice</h2>
          <div className="text-sm text-muted-foreground space-y-3 mb-6">
            <p>
              This tool provides <strong>educational information</strong> based on your uploaded collective bargaining agreement.
            </p>
            <p>
              <strong>It does not provide legal advice.</strong>
            </p>
            <p>
              Always consult HR, labor relations professionals, or legal counsel before taking any action based on information provided by this tool.
            </p>
            <p>
              Your uploaded agreement is <strong>private</strong>. It is used only to answer your questions and is not shared, published, or used to train any models.
            </p>
          </div>
          <button
            onClick={handleAccept}
            className="w-full bg-primary text-primary-foreground py-2 rounded-sm btn-lift font-medium"
            data-testid="accept-disclaimer-btn"
          >
            I Understand and Accept
          </button>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  const [creditModal, setCreditModal] = useState({ open: false, detail: null });
  const [planModal, setPlanModal] = useState({ open: false, detail: null });

  useEffect(() => {
    // Listen for 402 errors from API calls
    const handleCreditError = (event) => {
      setCreditModal({ open: true, detail: event.detail });
    };

    const handlePlanError = (event) => {
      setPlanModal({ open: true, detail: event.detail });
    };

    window.addEventListener('show-credit-modal', handleCreditError);
    window.addEventListener('show-plan-modal', handlePlanError);

    return () => {
      window.removeEventListener('show-credit-modal', handleCreditError);
      window.removeEventListener('show-plan-modal', handlePlanError);
    };
  }, []);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <MaintenanceMode>
          <Toaster position="top-right" richColors />
          <ImpersonationBanner />
          <AnnouncementBanner />
          <AnalyticsTracker />
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact-sales" element={<ContactSales />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/security" element={<Security />} />
          <Route path="/cbaiq-vs-chatgpt" element={<CbaiqVsChatgpt />} />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Billing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <Dashboard />
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <Upload />
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ask/:contractId?"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <Ask />
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <History />
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <SavedAnswers />
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <Settings />
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/org"
            element={
              <ProtectedRoute>
                <OrgDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Contract Expert Agent Routes */}
          <Route
            path="/agent/chat"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <ContractExpertWelcome>
                    <AgentChat />
                  </ContractExpertWelcome>
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/documents"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <ContractExpertWelcome>
                    <AgentDocuments />
                  </ContractExpertWelcome>
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/channels"
            element={
              <ProtectedRoute>
                <DisclaimerWrapper>
                  <ContractExpertWelcome>
                    <ConnectChannels />
                  </ContractExpertWelcome>
                </DisclaimerWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/pricing"
            element={<AgentPricing />}
          />
          <Route
            path="/agent/billing/success"
            element={
              <ProtectedRoute>
                <AgentBillingSuccess />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Portal Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<AdminLayout />}>
            <Route path="overview" element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="promotions" element={<AdminPromotions />} />
            <Route path="credits" element={<AdminCredits />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="traffic" element={<AdminAnalyticsDashboard />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="contracts" element={<AdminContracts />} />
            <Route path="qa" element={<AdminQA />} />
            <Route path="case-review" element={<AdminCaseReview />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="blog" element={<AdminBlogList />} />
            <Route path="blog/new" element={<AdminBlogEditor />} />
            <Route path="blog/:id" element={<AdminBlogEditor />} />
            <Route path="blog/ai" element={<AdminAIBlogSettings />} />
            {/* SaaS Intelligence Routes */}
            <Route path="saas-overview" element={<AdminSaaSOverview />} />
            <Route path="activation" element={<AdminActivation />} />
            <Route path="credit-profitability" element={<AdminCreditProfitability />} />
            <Route path="churn" element={<AdminChurnRisk />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="sales-leads" element={<AdminSalesLeads />} />
            <Route path="orgs" element={<AdminOrgs />} />
            <Route path="api-keys" element={<AdminAPIKeys />} />
            {/* Contract Expert Analytics */}
            <Route path="agent-analytics" element={<AdminAgentAnalytics />} />
            {/* Contract Expert Customer Success - Phase 6 */}
            <Route path="customer-success" element={<AdminCustomerSuccess />} />
            {/* Contract Expert Support Tools - Phase 7 */}
            <Route path="agent-support" element={<AdminAgentSupport />} />
            {/* Pilot Readiness Dashboard - Phase 7 */}
            <Route path="pilot-dashboard" element={<PilotReadinessDashboard />} />
          </Route>
          {/* Catch-all 404 (noindex) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        
        {/* Cookie Consent Banner */}
        <CookieConsent />
        
        {/* First-Run Trust Splash (only for authenticated users) */}
        <TrustSplashWrapper />

        {/* Credit Modals */}
        <InsufficientCreditsModal 
          isOpen={creditModal.open}
          onClose={() => setCreditModal({ open: false, detail: null })}
          errorDetail={creditModal.detail}
        />
        <PlanRequiredModal
          isOpen={planModal.open}
          onClose={() => setPlanModal({ open: false, detail: null })}
          errorDetail={planModal.detail}
        />
        </MaintenanceMode>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

// Wrapper to access auth context
const TrustSplashWrapper = () => {
  const { user } = useAuth();
  return user ? <TrustSplash /> : null;
};

export default App;
